/**
 * iter170 — Mounts on every page inside the Capacitor native shell. On the
 * web/PWA it is a complete no-op (the early `isNativeShell` check short-
 * circuits before any plugin code is loaded).
 *
 * Why a separate component instead of inlining into AuthContext?
 *   - We need `useNavigate` for the deep-link callback, which only works
 *     INSIDE the <BrowserRouter> tree.
 *   - We want to wait until `user` is non-null so token registration is
 *     tied to an authenticated session.
 *   - Keeping it isolated makes the web bundle treeshake the native plugin
 *     out cleanly when it's never invoked.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { initNativePush, disposeNativePush } from "../lib/nativePush";

export default function NativePushBootstrap() {
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!user?.user_id) {
            // Logged out (or web) — release listeners.
            disposeNativePush();
            return;
        }
        let cancelled = false;
        (async () => {
            const res = await initNativePush({
                navigate: (path) => {
                    if (!cancelled) navigate(path);
                },
                onForeground: (notif) => {
                    // System banners are suppressed in foreground on most
                    // Android devices when only `data` payload is used —
                    // surface a sonner toast so the user still sees it.
                    const title = (notif && notif.title) || "New notification";
                    const body  = (notif && notif.body)  || "";
                    try { toast(`${title}${body ? " · " + body : ""}`); } catch { /* */ }
                },
            });
            // Surface a one-time toast when the user explicitly declines
            // permission, so they know to re-enable from system settings.
            if (!cancelled && res && res.initialized === false && res.reason === "permission-denied") {
                try { toast("Push notifications declined · enable in system settings to receive alerts"); } catch { /* */ }
            }
        })();
        return () => { cancelled = true; };
    }, [user?.user_id, navigate]);

    return null;
}
